import './EventLogTable.scss';

import { mdiArrowRight, mdiChevronDown, mdiChevronUp } from '@mdi/js';
import type { InfiniteData } from '@tanstack/react-query';
import { type TableColumnsType, Table } from 'antd';
import type { CappedTableResultOfEventLogEntry, EventLogEntry } from 'api/models/api';
import type { User } from 'api/models/mainservice';
import type { AuditEventType, FilterValue } from 'common/models';
import Icon from 'components/Icon/Icon';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import Link from 'components/NavLink/Link';
import { getFullName } from 'helpers/contactPersonHelpers';
import { getEventIcon, getEventLink } from 'helpers/eventsHelpers';
import { renderDateTime } from 'helpers/timeHelpers';
import { useCustomTranslation } from 'helpers/translationHelper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

interface EventLogTableProps {
  data?: InfiniteData<CappedTableResultOfEventLogEntry>;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  onTableChange?: (filters: Partial<Record<string, FilterValue | null>>) => void;
  timezoneId: string;
  users?: User[];
  disableNavigateFromEvent?: boolean;
}

const EventLogTable = ({
  data,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  onTableChange,
  timezoneId,
  users,
  disableNavigateFromEvent
}: EventLogTableProps) => {
  const { tAudit } = useCustomTranslation();
  const [expandedRowKeys, setExpandedRowKeys] = useState<readonly (string | number)[]>([]);

  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, inView, isFetchingNextPage]);

  const columns = useMemo<TableColumnsType<EventLogEntry>>(
    () => [
      {
        key: 'eventName',
        dataIndex: 'eventName',
        title: tAudit('event_name'),
        render: (_, record) => {
          const icon = getEventIcon(record.eventType as AuditEventType);

          return (
            <span className="EventLogTable__event-name">
              <div className={`EventLogTable__event-icon ${icon.className}`}>
                <Icon path={icon.icon} className={icon.className} />
              </div>
              <span>{record.eventName}</span>
            </span>
          );
        }
      },
      {
        key: 'headlineName',
        dataIndex: 'headlineName',
        title: tAudit('headline_name'),
        onCell: () => ({ className: 'EventLogTable__nowrap-cell' }),
        render: (_, record) => {
          const link = getEventLink(record);

          if (record.headlineName && link && !disableNavigateFromEvent) {
            return <Link label={record.headlineName} pathname={link} />;
          }

          if (disableNavigateFromEvent) {
            return record.headlineName;
          }

          return undefined;
        }
      },
      {
        key: 'actorName',
        dataIndex: 'actorName',
        title: tAudit('actor_name'),
        onCell: () => ({ className: 'EventLogTable__nowrap-cell' }),
        filters: users
          ? users.map((user) => ({ text: getFullName(user), value: user.id }))
          : undefined
      },
      {
        key: 'createdAt',
        dataIndex: 'createdAt',
        title: tAudit('created_at'),
        onCell: () => ({ className: 'EventLogTable__nowrap-cell' }),
        render: (_, record) => renderDateTime(record.createdAt, timezoneId)
      },
      {
        key: 'expand'
      }
    ],
    [timezoneId, tAudit, users, disableNavigateFromEvent]
  );

  const renderRecordFields = useCallback(
    (record: EventLogEntry) => {
      return (
        <div className="EventLogTable_fields">
          <div className="EventLogTable_fields-title">{tAudit('event_fields_info')}</div>
          <div className="EventLogTable_fields-grid">
            {record.fields?.map((field) => (
              <div className="EventLogTable_field" key={field.field}>
                <div className="EventLogTable_field-label">{field.label}</div>
                <div className="EventLogTable_field-values">
                  {field.oldDisplay && (
                    <>
                      <div className="EventLogTable_field-oldDisplay">{field.oldDisplay}</div>
                      <Icon path={mdiArrowRight} />
                    </>
                  )}
                  <div
                    className={`EventLogTable_field-newDisplay${field.oldDisplay ? ' green' : ''}`}
                  >
                    {field.newDisplay}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    },
    [tAudit]
  );

  const dataSource: EventLogEntry[] =
    data?.pages.reduce((acc: EventLogEntry[], page) => [...acc, ...(page.data || [])], []) || [];

  return (
    <div>
      <Table
        className="EventLogTable"
        columns={columns}
        dataSource={dataSource}
        loading={isLoading}
        rowClassName={(record) =>
          expandedRowKeys.includes(record.id) ? 'EventLogTable__parent-row-expanded' : ''
        }
        rowKey="id"
        pagination={false}
        scroll={{ x: true }}
        onChange={(_, filters) => onTableChange?.(filters as any)}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as (string | number)[]),
          expandedRowRender: renderRecordFields,
          expandIconColumnIndex: 4,
          expandIcon: (props) =>
            props.expanded ? (
              <Icon
                path={mdiChevronUp}
                className="EventLogTable_expand-icon"
                onClick={(e) => props.onExpand(props.record, e)}
              />
            ) : (
              <Icon
                path={mdiChevronDown}
                className="EventLogTable_expand-icon"
                onClick={(e) => props.onExpand(props.record, e)}
              />
            )
        }}
      />
      {isFetchingNextPage && <LoadingIndicator />}
      <div ref={ref} />
    </div>
  );
};

export default EventLogTable;
